<template>
  <app-custom-dialog
    :value="value"
    :footer="true"
    :title="
      initialLines && initialLines.length
        ? 'buffet_edit_plan'
        : 'buffet_add_plan'
    "
    submit-text="confirm"
    cancel-text="cancel"
    width="560px"
    title-icon="fal fa-layer-group"
    title-icon-color="primary"
    @input="$emit('input', $event)"
    @submit="apply"
  >
    <div slot="activator" class="buffet-plan-picker-body">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mb-4 buffet-plan-picker-progress"
      />
      <div
        v-for="(row, idx) in rows"
        :key="idx"
        class="buffet-plan-picker-row mb-4"
      >
        <div class="d-flex align-start">
          <div class="flex-grow-1 min-w-0">
            <TextSelect
              label="buffet_plan_name"
              :errorMessages="[]"
              v-model="row.planId"
              itemValue="id"
              text="la_name"
              :items="planSelectItems"
              :return-object="false"
              :disabled="loading || !catalog.length"
            />
            <CurrencyInput
              class="mt-3"
              label="buffet_persons"
              v-model="row.persons"
            />
          </div>
        </div>
      </div>
    </div>
  </app-custom-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import CurrencyInput from "@/components/CurrencyInput";
import TextSelect from "@/components/global/TextSelect";

export default {
  name: "BuffetPlanPicker",
  components: { CurrencyInput, TextSelect },
  mixins: [myMixin],
  props: {
    value: { type: Boolean, default: false },
    /** Current lines from cart (buffet_plan_id, persons, …) */
    initialLines: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      catalog: [],
      rows: [{ planId: null, persons: 1 }],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    branchId() {
      const b = this.user?.branch || this.user?.branch_id;
      return b?.id ?? "";
    },
    planSelectItems() {
      return this.catalog.map((p) => ({
        id: p.id,
        la_name: `${p.name} (${this.formatMoney(p.price)})`,
      }));
    },
  },
  watch: {
    value(open) {
      if (open) {
        if (this.initialLines && this.initialLines.length) {
          this.rows = this.initialLines.map((l) => ({
            planId: l.buffet_plan_id != null ? Number(l.buffet_plan_id) : null,
            persons: Math.max(1, Number(l.persons) || 1),
          }));
        } else {
          this.rows = [{ planId: null, persons: 1 }];
        }
        void this.fetchCatalog();
      }
    },
  },
  methods: {
    formatMoney(n) {
      return this.$options.filters.NumberFormat(Number(n || 0));
    },
    async fetchCatalog() {
      if (!this.user?.stores?.id) return;
      this.loading = true;
      try {
        const { data } = await this.$http.get("buffet_plans", {
          params: {
            store_id: this.user.stores.id,
            branch_id: this.branchId,
          },
        });
        if (data && data.status && Array.isArray(data.data)) {
          this.catalog = data.data.filter((p) => p.is_active !== 0);
        } else {
          this.catalog = [];
        }
      } catch (e) {
        this.catalog = [];
      } finally {
        this.loading = false;
      }
    },
    addRow() {
      this.rows.push({ planId: null, persons: 1 });
    },
    removeRow(idx) {
      if (this.rows.length < 2) return;
      this.rows.splice(idx, 1);
    },
    apply() {
      const lines = [];
      for (const row of this.rows) {
        const planId = row.planId != null ? Number(row.planId) : 0;
        const persons = Math.max(1, Math.round(Number(row.persons) || 1));
        if (planId < 1) continue;
        const plan = this.catalog.find((p) => Number(p.id) === planId);
        if (!plan) continue;
        const unit = Number(plan.price) || 0;
        const includedCategoryIds = Array.isArray(plan.categories)
          ? plan.categories.map((c) => Number(c.id)).filter((id) => id > 0)
          : [];
        lines.push({
          buffet_plan_id: planId,
          plan_name: plan.name,
          unit_price: unit,
          persons,
          subtotal: unit * persons,
          included_category_ids: includedCategoryIds,
        });
      }
      if (!lines.length) {
        toast(this, "buffet_select_plan", "error");
        return;
      }
      this.$emit("applied", lines);
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.buffet-plan-picker-progress {
  margin-top: -4px;
}
.min-w-0 {
  min-width: 0;
}
</style>
